// src/components/PickingModule.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext'; // Para acceder al usuario logueado

function PickingModule() {
  const { user, token } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState(''); // Estado para mensajes de advertencia

  // --- ESTADOS PARA EL MODAL ---
  const [isPickingOrderModalOpen, setIsPickingOrderModalOpen] = useState(false);
  const [selectedPickingOrder, setSelectedPickingOrder] = useState(null);
  
  // Estados para filtros
  const [cotizacionFilter, setCotizacionFilter] = useState('sin');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);
  const ordersPerPage = 15;

  // Helper para validar si un número de guía es "válido" para transiciones de estado
  const isValidGuideForTransition = (guideNumber) => {
    if (!guideNumber || typeof guideNumber !== 'string') return false;
    const cleanedGuide = guideNumber.trim().toUpperCase();
    return cleanedGuide !== '' &&
           cleanedGuide !== 'N/A' &&
           !/^GUIA-\d+-\d+$/.test(guideNumber) && // Nuestro placeholder
           !cleanedGuide.includes('PENDING') &&
           !cleanedGuide.includes('GENERATING');
  };

  // --- FUNCIÓN REUTILIZABLE PARA GENERAR HTML DEL TICKET ---
  const generatePrintHtml = (order) => { 
    if (!order) return '';

    const today = new Date();
    const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()} ${today.getHours().toString().padStart(2, '0')}:${today.getMinutes().toString().padStart(2, '0')}`;

    const groupedItems = {};
    order.items_a_surtir.forEach(item => {
      if (!groupedItems[item.sku]) {
        groupedItems[item.sku] = {
          sku: item.sku,
          descripcion: item.descripcion,
          total_cantidad: 0,
          almacenes_surtidos: []
        };
      }
      groupedItems[item.sku].total_cantidad += item.cantidad;
      groupedItems[item.sku].almacenes_surtidos.push(`${item.almacen}: ${item.cantidad}pza`);
    });

    let itemsListHtml = '';
    Object.values(groupedItems).forEach(item => {
      itemsListHtml += `
        <div class="item-entry">
          <p style="margin: 2mm 0; line-height: 1.2;">
            <strong style="font-size: 12pt;">Cant: ${item.total_cantidad}</strong> - 
            <span style="font-size: 11pt; word-break: break-all; white-space: normal;">${item.descripcion} (${item.sku})</span>
          </p>
          <p style="margin: 2mm 0; line-height: 1.2; font-size: 11pt; font-weight: bold; color: #000000ff; word-break: break-all;">
            Almacén: ${item.almacenes_surtidos.join(', ')}
          </p>
        </div>
      `;
    });

    let pendingItemsSection = '';
    if (order.items_pendientes_inventario && order.items_pendientes_inventario.length > 0) {
      let pendingItemsRows = '';
      order.items_pendientes_inventario.forEach(item => {
        pendingItemsRows += `<div style="font-size: 7pt; margin-bottom: 1mm; line-height: 1.1;">- ${item.sku}: Faltan ${item.cantidad_faltante} (${item.razon})</div>`;
      });
      pendingItemsSection = `
        <div style="margin-top: 3mm; border-top: 1px dashed #ccc; padding-top: 2mm;">
          <h3 style="font-size: 9pt; font-weight: bold; margin-bottom: 2mm; color: orange;">PENDIENTES:</h3>
          ${pendingItemsRows}
        </div>
      `;
    }

    // --- Lógica mejorada para el mensaje de impresión ---
    let printStatusMessageHtml = ''; 
    const printCount = parseInt(order.picking_print_count, 10); // Asegurarse de que sea un número
    console.log(`DEBUG: picking_print_count en generatePrintHtml: ${printCount}, Tipo: ${typeof printCount}`); // LOG DE DEPURACIÓN
    if (printCount) { // Si hay un contador de impresiones (>= 1)
      const lastPrintedDate = order.picking_last_printed_at ? new Date(order.picking_last_printed_at).toLocaleString() : 'N/A';
      
      if (printCount === 1) {
        printStatusMessageHtml = `
          <p style="font-size: 11pt; font-weight: bold; margin: 3mm 0; text-align: center; border: 1px solid #000; padding: 1mm;">
            PRIMERA IMPRESIÓN (Fecha: ${lastPrintedDate})
          </p>
        `;
      } else { // picking_print_count > 1 (segunda impresión o más)
        printStatusMessageHtml = `
          <p style="font-size: 11pt; font-weight: bold; margin: 3mm 0; text-align: center; border: 2px solid #000; padding: 1mm;">
            REIMPRESIÓN #${printCount} (Última vez: ${lastPrintedDate})
          </p>
        `;
      }
    }


    return `
      <div class="ticket-container">
        <div class="header">
          <h1 style="font-size: 18pt; font-weight: bold; margin: 0;">BLUEROVER</h1><br><br>
          <p style="font-size: 12pt; margin: 1mm 0; font-weight: bold;">Orden de Surtido</p>
          <p style="font-size: 11pt; margin: 1mm 0;">Fecha: ${formattedDate}</p>
          ${printStatusMessageHtml}
        </div>
        <div class="info-section">
          <p style="margin: 1.5mm 0; font-size: 11pt;"><strong>Pedido Ref:</strong> ${order.referencia_plataforma}</p>
          <p style="margin: 1.5mm 0; font-size: 11pt;"><strong>Folio AZ:</strong> ${order.folio_autoazur}</p>
          <p style="margin: 1.5mm 0; font-size: 11pt;"><strong>Plataforma:</strong> ${order.canal_origen}</p>
          <p style="margin: 1.5mm 0; font-size: 11pt;"><strong>Cotización:</strong> <span style="font-size: 13pt;">${order.picking_quote_number || 'N/A'}</span></p>
          <p style="margin: 1.5mm 0; font-size: 11pt;"><strong>Estado:</strong> ${order.nuevo_estado_pedido}</p>
          <p style="margin: 1.5mm 0; font-size: 11pt;"><strong>Paquetería:</strong> ${order.metodo_envio || 'N/A'}</p>
        </div>
        <hr style="border: none; border-top: 1px dashed #000; margin: 3mm 0;">
        <div class="items-list-section">
            <h3 style="font-size: 11pt; font-weight: bold; margin-bottom: 3mm; text-decoration: underline;">Detalle de Artículos:</h3>
            ${itemsListHtml}
        </div>
        <hr style="border: none; border-top: 1px dashed #000; margin: 3mm 0;">
        <div class="total-line" style="font-size: 12pt;">Total ítems: ${order.items_a_surtir.reduce((sum, item) => sum + item.cantidad, 0)}</div>
        ${pendingItemsSection}
        <hr style="border: none; border-top: 1px dashed #000; margin: 4mm 0;">
        <div class="footer">
          <p style="margin-bottom: 2mm;">__________________________</p>
          <p style="font-size: 10pt; font-weight: bold;">Firma de Recibido</p>
        </div>
      </div>
    `;
  };

  // --- FUNCIÓN PARA ABRIR EL DIÁLOGO DE IMPRESIÓN ---
  const openPrintDialog = (htmlContent) => {
    const printContainer = document.createElement('div');
    printContainer.className = 'print-only-ticket-container';
    printContainer.innerHTML = htmlContent;
    document.body.appendChild(printContainer);

    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = `
      @media print {
        html, body { margin: 0 !important; padding: 0 !important; height: auto !important; overflow: hidden !important; }
        body * { visibility: hidden !important; }
        .print-only-ticket-container, .print-only-ticket-container * { visibility: visible !important; }
        .print-only-ticket-container {
          position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; max-width: 80mm !important;
          margin: 0 auto !important; padding: 5mm !important; box-sizing: border-box !important; display: block !important;
          background-color: white !important; font-family: 'Arial', sans-serif !important; font-size: 12pt !important; line-height: 1.4 !important;
        }
        .ticket-container { width: 100% !important; max-width: 80mm !important; margin: 0 auto !important; padding: 2mm !important; box-sizing: border-box !important; }
        .header { text-align: center !important; margin-bottom: 6mm !important; }
        .header h1 { font-size: 20pt !important; margin: 0 !important; }
        .header p { font-size: 13pt !important; margin: 2mm 0 !important; }
        .info-section { margin-bottom: 6mm !important; padding-bottom: 4mm !important; font-size: 12pt !important; }
        .info-section p { margin: 2mm 0 !important; }
        .items-list-section { margin-bottom: 6mm !important; }
        .item-entry {
          margin-bottom: 3mm !important; padding-bottom: 2mm !important; border-bottom: 0.5px dashed #000 !important;
          line-height: 1.4 !important; word-break: break-all !important; white-space: normal !important;
        }
        .item-entry p { margin: 0 !important; }
        .total-line { text-align: right !important; font-weight: bold !important; margin-top: 4mm !important; font-size: 12pt !important; }
        .footer { text-align: center !important; margin-top: 8mm !important; font-size: 10pt !important; }
        .footer p { margin: 0 !important; }
      }
    `;
    document.head.appendChild(styleSheet);

    window.print();

    setTimeout(() => {
      document.body.removeChild(printContainer);
      document.head.removeChild(styleSheet);
    }, 1000);
  };


  const fetchPickingOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!user || !token) {
        setError('Acceso denegado. Por favor, inicia sesión de nuevo.');
        setLoading(false);
        return;
      }
      
      const queryParams = new URLSearchParams({
          cotizacionStatus: cotizacionFilter,
          page: currentPage,
          limit: ordersPerPage
      });
      if (fromDate) queryParams.append('fromDate', fromDate);
      if (toDate) queryParams.append('toDate', toDate);

      const response = await fetch(`/api/picking/orders-for-picking?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });
      const data = await response.json();

      if (response.ok) {
        setOrders(data.orders);
        setTotalOrdersCount(data.totalOrders);
        setTotalPages(data.totalPages);
      } else {
        setError(data.mensaje || 'Error al cargar pedidos.');
      }
    } catch (err) {
      setError('Error de conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  }, [user, token, cotizacionFilter, currentPage, ordersPerPage, fromDate, toDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [cotizacionFilter, fromDate, toDate]);

  useEffect(() => {
    fetchPickingOrders();
  }, [fetchPickingOrders]);

  const handleGeneratePickingOrder = async (orderId) => {
    setMessage('');
    setWarningMessage(''); // Limpiar advertencias anteriores
    setLoading(true);
    try {
  const base = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${base}/api/picking/generate-picking-order`,  {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ order_id: orderId }),
      });
      const data = await response.json();
      
      if (response.ok) {
        // Si hay una advertencia del backend, la mostramos
        if (data.warning) {
          setWarningMessage(data.warning);
        } else {
          setMessage(data.mensaje);
        }
        setSelectedPickingOrder(data); // Actualiza el estado con los datos frescos
        setIsPickingOrderModalOpen(true); // Abre el modal
        
        // --- CORRECCIÓN CLAVE AQUÍ: NO LLAMAR A LA IMPRESIÓN AUTOMÁTICAMENTE ---
        // El usuario debe hacer clic en el botón "Imprimir Ticket" del modal o de la tabla.
        // const htmlContent = generatePrintHtml(data);
        // openPrintDialog(htmlContent);

        fetchPickingOrders(); // Refrescar la lista de pedidos después de la generación
      } else {
        // Manejar el error específico de "sin guía válida"
        if (data.mensaje.includes("no tiene una guía válida")) {
            setMessage(`Error: ${data.mensaje}`);
        } else {
            setMessage(`Error: ${data.mensaje || 'Error al generar la orden.'}`);
        }
      }
    }
    catch (err) {
      console.error('[Picking] generate error:', err);
      setMessage('Error de conexión al generar la orden.');
    } finally {
      setLoading(false);
    }
  };
  
  // --- NUEVA FUNCIÓN: Maneja la impresión/reimpresión desde el modal ---
  const handlePrintOrReprintFromModal = async (orderId) => {
    setMessage('');
    setWarningMessage('');
    setLoading(true);
    try {
        const response = await fetch(`/api/picking/reprint-picking-order/${orderId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        console.log(`DEBUG: [Frontend - handlePrintOrReprintFromModal] Datos recibidos del backend:`, data);
        if (response.ok) {
            const htmlContent = generatePrintHtml(data); // Generar HTML con los datos actualizados
            openPrintDialog(htmlContent); // Abrir diálogo de impresión
            setSelectedPickingOrder(data); // Actualizar el estado del modal con los datos frescos
            fetchPickingOrders(); // Refrescar la lista de pedidos en la tabla principal
        } else {
            setMessage(`Error al imprimir/reimprimir: ${data.mensaje}`);
        }
    } catch (err) {
        setMessage('Error de conexión al imprimir/reimprimir la orden.');
    } finally {
        setLoading(false);
    }
  };


  const handleReprintPickingOrder = async (orderId) => { // Este es el botón de la tabla
    setMessage('');
    setWarningMessage(''); // Limpiar advertencias anteriores
    setLoading(true);
    try {
        const response = await fetch(`/api/picking/reprint-picking-order/${orderId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        // --- Depuración: Datos recibidos en handleReprintPickingOrder ---
        console.log(`DEBUG: [Frontend - handleReprintPickingOrder] Datos recibidos del backend:`, data);
        if (response.ok) {
            const htmlContent = generatePrintHtml(data); // Generar HTML con los datos actualizados
            openPrintDialog(htmlContent); // Abrir diálogo de impresión
            // CORRECCIÓN: Refrescar la lista de pedidos después de reimprimir
            fetchPickingOrders(); 
        } else {
            setMessage(`Error al reimprimir: ${data.mensaje}`);
        }
    } catch (err) {
        setMessage('Error de conexión al reimprimir la orden.');
    } finally {
        setLoading(false);
    }
  };

  // NUEVA FUNCIÓN: Refrescar el estado de un pedido individual
  const handleRefreshOrderStatus = async (orderId) => {
    setMessage('');
    setWarningMessage('');
    setLoading(true);
    try {
      const response = await fetch(`/api/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (response.ok) {
        // Actualizar el pedido específico en el estado 'orders'
        setOrders(prevOrders => prevOrders.map(order => 
          order.id === orderId ? data : order
        ));
        setMessage(`Pedido ${data.referencia_plataforma} refrescado exitosamente.`);
      } else {
        setMessage(`Error al refrescar pedido: ${data.mensaje || 'Error desconocido'}`);
      }
    } catch (err) {
      setMessage('Error de conexión al refrescar el pedido.');
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(''), 3000); // Limpiar mensaje después de 3 segundos
    }
  };
  
  // ****** INICIO DE LA NUEVA FUNCIÓN ******
  // Función para resetear la cotización de un pedido
  const handleResetQuote = async (orderId) => {
    setMessage('');
    setWarningMessage('');
    
    // Pedir confirmación al usuario
    if (!window.confirm(`¿Estás seguro de que quieres eliminar la cotización de este pedido? Esta acción no se puede deshacer y el pedido volverá a la lista de "Sin Cotización".`)) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`/api/picking/reset-quote/${orderId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });
      const data = await response.json();

      if (response.ok) {
        setMessage(data.mensaje);
        fetchPickingOrders(); // Refrescar la lista para que el pedido se mueva a "Sin Cotización"
      } else {
        setMessage(`Error al resetear la cotización: ${data.mensaje || 'Error desconocido'}`);
      }
    } catch (err) {
      setMessage('Error de conexión al resetear la cotización.');
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(''), 5000); // Limpiar el mensaje después de 5 segundos
    }
  };
  // ****** FIN DE LA NUEVA FUNCIÓN ******

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // --- COMPONENTE DEL MODAL RESTAURADO ---
  const PickingOrderDetailsModal = ({ pickingOrderData, onClose }) => {
    if (!pickingOrderData) return null;

    // --- Depuración: pickingOrderData recibido en el modal ---
    console.log(`DEBUG: [Frontend - PickingOrderDetailsModal] pickingOrderData recibido en el modal:`, pickingOrderData);

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="bg-white p-6 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onClose}
            className="absolute top-2 right-2 bg-gray-800 text-white rounded-full h-8 w-8 flex items-center justify-center text-xl font-bold hover:bg-gray-700 transition duration-200"
            title="Cerrar"
          >
            &times;
          </button>
          <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">Orden de Surtido Generada</h2>
          <p className="text-gray-700 mb-4 text-center">{pickingOrderData.mensaje}</p>

          {pickingOrderData.warning && ( // Mostrar advertencia si existe
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4" role="alert">
              <p className="font-bold">¡Advertencia!</p>
              <p>{pickingOrderData.warning}</p>
            </div>
          )}

          <div className="bg-gray-50 p-4 rounded-lg shadow-inner">
            <p><strong>Pedido:</strong> {pickingOrderData.referencia_plataforma}</p>
            <p><strong>Estado:</strong> <span className={`font-bold ${pickingOrderData.nuevo_estado_pedido === 'Inventario Pendiente' ? 'text-orange-600' : 'text-green-600'}`}>{pickingOrderData.nuevo_estado_pedido}</span></p>
            <p><strong>Cotización:</strong> {pickingOrderData.picking_quote_number || 'N/A'}</p>
          </div>
          
          <div className="flex justify-center mt-6 space-x-4">
            <button
              onClick={() => handlePrintOrReprintFromModal(pickingOrderData.order_id)} // Llama a la nueva función
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline transition duration-200"
            >
              Imprimir Ticket
            </button>
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline transition duration-200"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* NO ES NECESARIO INCLUIR <style> AQUÍ, SE INYECTA EN LA VENTANA DE IMPRESIÓN */}

      <h2 className="text-2xl font-bold text-gray-800 mb-6">Generar Orden de Surtido (Picking)</h2>
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="flex flex-wrap justify-between items-center mb-4 gap-4">
            <h3 className="text-xl font-semibold text-gray-800">Pedidos Pendientes de Surtido</h3>
            <div className="flex flex-wrap items-center space-x-4">
                <div className="flex items-center space-x-2">
                    <label htmlFor="fromDate" className="text-sm font-medium text-gray-700">Desde:</label>
                    <input
                        type="date"
                        id="fromDate"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="shadow-sm border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                <div className="flex items-center space-x-2">
                    <label htmlFor="toDate" className="text-sm font-medium text-gray-700">Hasta:</label>
                    <input
                        type="date"
                        id="toDate"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="shadow-sm border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">Cotización:</span>
                    <select value={cotizacionFilter} onChange={(e) => setCotizacionFilter(e.target.value)} className="rounded-md border-gray-300 shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500">
                        <option value="sin">Sin Cotización</option>
                        <option value="con">Con Cotización</option>
                        <option value="todos">Todos</option>
                    </select>
                </div>
            </div>
        </div>

        {message && (
          <div className={`p-3 rounded-lg text-sm font-medium mb-4 ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
            {message}
          </div>
        )}
        {warningMessage && ( // Mostrar el mensaje de advertencia aquí
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4" role="alert">
            <p className="font-bold">¡Advertencia!</p>
            <p>{warningMessage}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center text-gray-600 text-lg py-8">Cargando pedidos...</div>
        ) : error ? (
          <div className="text-center text-red-600 text-lg py-8">Error: {error}</div>
        ) : orders.length === 0 ? (
          <p className="text-center text-gray-600">No hay pedidos que coincidan con los filtros seleccionados.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"># Pedido</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Folio AZ</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Plataforma</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cotización</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Estado Guía</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => {
                    const hasValidGuide = order.guides.some(guide => isValidGuideForTransition(guide.guide_number));
                    const isInitialPickingState = ['Inventario Pendiente', 'Confirmada'].includes(order.estado_interno); // 'Recibido' ya no existe
                    const disableForNoGuide = isInitialPickingState && !hasValidGuide;
                    const disableReason = disableForNoGuide ? `Pedido en estado "${order.estado_interno}" sin guía válida. Adjunte una guía primero.` : '';

                    return (
                      <tr key={order.id}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium">{order.referencia_plataforma}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">{order.folio_autoazur}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">{order.canal_origen}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">{order.picking_quote_number || 'N/A'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">{new Date(order.fecha_venta).toLocaleDateString()}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-center text-sm">
                          {hasValidGuide ? (
                            <span className="text-green-600 font-medium">Con Guía ✅</span>
                          ) : (
                            <span className="text-red-600 font-medium">Sin Guía ❌</span>
                          )}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-center text-sm font-medium">
                          {order.picking_quote_number ? (
                            <>
                            <button
                              onClick={() => handleReprintPickingOrder(order.id)}
                              disabled={loading}
                              className="text-blue-600 hover:text-blue-800 disabled:opacity-50 mr-2" // Añadido margen
                              title="Reimprimir Orden de Surtido"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                              </svg>
                            </button>
                            {/* ****** INICIO DEL NUEVO BOTÓN ****** */}
                            <button
                              onClick={() => handleResetQuote(order.id)}
                              disabled={loading}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50 mr-2"
                              title="Resetear Cotización"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                            {/* ****** FIN DEL NUEVO BOTÓN ****** */}
                            </>
                          ) : (
                            <button
                              onClick={() => handleGeneratePickingOrder(order.id)}
                              disabled={loading || disableForNoGuide}
                              className="bg-green-600 hover:bg-green-700 text-white font-bold py-1 px-3 rounded-lg text-xs transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed mr-2" // Añadido margen
                              title={disableReason}
                            >
                              Generar Orden
                            </button>
                          )}
                          {/* Botón de Refrescar Guía */}
                          <button
                            onClick={() => handleRefreshOrderStatus(order.id)}
                            disabled={loading}
                            className="text-gray-600 hover:text-gray-800 disabled:opacity-50"
                            title="Refrescar estado de guía"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m0 0H5" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* --- CONTROLES DE PAGINACIÓN --- */}
            <div className="flex justify-between items-center pt-4 border-t mt-4">
                <p className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{orders.length > 0 ? ((currentPage - 1) * ordersPerPage) + 1 : 0}</span> - <span className="font-medium">{Math.min(currentPage * ordersPerPage, totalOrdersCount)}</span> de <span className="font-medium">{totalOrdersCount}</span> pedidos
                </p>
                <div className="flex items-center space-x-1">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1} 
                        className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        &laquo;
                    </button>
                    <span className="px-3 py-1 text-gray-700">
                        Página {currentPage} de {totalPages}
                    </span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        &raquo;
                    </button>
                </div>
            </div>
          </>
        )}
      </div>

      {/* --- RENDERIZADO DEL MODAL RESTAURADO --- */}
      {isPickingOrderModalOpen && selectedPickingOrder && (
        <PickingOrderDetailsModal
          pickingOrderData={selectedPickingOrder}
          onClose={() => setIsPickingOrderModalOpen(false)}
        />
      )}
    </>
  );
}

export default PickingModule;
